import * as React from "react";
import { ColumnProps, Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from 'primereact/inputtext';

import { resolveFieldData } from '../../../../helpers/utils/FieldDataResolver';
import { ColumnFilterWrapper } from '../../../synced/ui/ColumnFilterWrapper/ColumnFilterWrapper';

export type TableColumn = ColumnProps & {
  type?: TableColumnType;
  disableEdit?: boolean;
  url?: ColumnURLProps;
  filterElementFactory?: (dtRef: React.RefObject<DataTable<any> | null>, filters?: any) => React.ReactNode;
  filters?: any;
}

export type TableDtRefColumn = TableColumn & {
  dtRef: React.RefObject<DataTable<any> | null>;
}

export interface ColumnURLProps{
  field: string;
  href ?: boolean;
  targetBlank?: boolean;
  condition?: URLConditionProps;
  getLinkURL?: (rowData) => string;
  onClick?: (rowData) => void;
}

export interface URLConditionProps{
  show: (rowData) => boolean;
  field: string;
}

export enum TableColumnType {
  string,
  date,
  number,
  enum
}

export const DefaultColumn: React.FC<TableDtRefColumn> = (properties: TableDtRefColumn) => {
  const bodyTemplate = (row, col) => {
    const headerContent = typeof col.header === 'function'
      ? col.header({ props: properties.dtRef.current?.props })
      : col.header;

    return (
      <React.Fragment>
        {headerContent && <span className="column-header">{headerContent}</span>}
        {properties.body ? (properties.body as Function)(row, col) : resolveFieldData(row, col.field)}
      </React.Fragment>
    );
  }


  const [value, setValue] = React.useState<string>(``);
  const filterKey = properties.filterField ?? properties.field ?? ``;
  const filterEntry = (properties.filters?.[filterKey] as any);
  React.useEffect(() => {
    const existing = filterEntry?.value;
    if (existing === undefined || existing === null || existing === ``)
      return;

    setValue(existing);
  }, [filterEntry, filterKey]);


  const typingTimeoutRef = React.useRef(0);

  const onFilterChange = (event) => {
    const newValue = event.target.value as string;
    setValue(newValue);

    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = window.setTimeout(() => {
      properties.dtRef.current?.filter(newValue, properties.filterField ?? properties.field ?? ``, 'contains');
    }, 500);
  }

  const onClearFilter = () => {
    clearTimeout(typingTimeoutRef.current);
    setValue(``);
    properties.dtRef.current?.filter(``, properties.filterField ?? properties.field ?? ``, 'contains');
  }

  const renderFilter = () => {
    return (
      <ColumnFilterWrapper showClear={!!value} onClear={onClearFilter}>
        <InputText
          className="p-column-filter"
          value={value}
          onChange={onFilterChange} />
      </ColumnFilterWrapper>
    );
  }

  const resolvedFilterElement = properties.filterElementFactory
    ? properties.filterElementFactory(properties.dtRef, properties.filters)
    : properties.filterElement;

  return (
    <Column
      sortable={true}
      filter={true}
      filterMatchMode="contains"
      {...properties}
      showClearButton={false}
      filterElement={resolvedFilterElement ?? renderFilter()}
      bodyClassName={`pda-column pda-column-${properties.field?.replace('.','-')} ${
        properties.bodyClassName ?? ``
      }`}
      headerClassName={`pda-column-header ${`pda-column-header-${properties.field?.replace('.', '-')}`} ${
        properties.headerClassName ?? ``
      }`}
      body={bodyTemplate}
      filterHeaderClassName={`pda-filter-${properties.field}`} />
  );
};
